import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR
} from "@angular/forms";
import { TUI_SANITIZER } from "@taiga-ui/core";
import { NgDompurifySanitizer } from "@tinkoff/ng-dompurify";
import { isNotEmptyValue } from "kwikid-toolkit";
import { Subscription } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";
import { throwErrorMessage } from "../../../../helpers/kwikui.common.helpers";
import { DEFAULT_VALUES, VALIDATE_KEY_VALUES } from "./input-files.constants";
import { EKwikUIInputFileState } from "./input-files.definitions";
import { isValidKeyValue } from "./input-files.validation";

@Component({
  selector: "kwikui-input-files",
  templateUrl: "./input-files.component.html",
  styleUrls: ["./input-files.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KwikUIInputFilesComponent),
      multi: true
    },
    {
      provide: TUI_SANITIZER,
      useClass: NgDompurifySanitizer
    }
  ]
})
export class KwikUIInputFilesComponent
  implements OnInit, OnChanges, ControlValueAccessor {
  @Input() accept: string = DEFAULT_VALUES.accept;

  @Input() class: string = DEFAULT_VALUES.class;

  @Input() disabled: boolean = DEFAULT_VALUES.disabled;

  @Input() files: any[] = [];

  @Input() formControl: FormControl = new FormControl({});

  @Input() formControlName: string = DEFAULT_VALUES.formControlName;

  @Input() icon: string = DEFAULT_VALUES.icon;

  @Input() id: string = DEFAULT_VALUES.id;

  @Input() label: string = DEFAULT_VALUES.label;

  @Input() link: string = DEFAULT_VALUES.link;

  @Input() maxFileSize: number = DEFAULT_VALUES.maxFileSize;

  @Input() multiple: boolean = DEFAULT_VALUES.multiple;

  @Input() setFileTypesToForm: string[] = DEFAULT_VALUES.setFileTypesToForm;

  @Input() showAddedFiles: boolean = DEFAULT_VALUES.showAddedFiles;

  @Input() size: string = DEFAULT_VALUES.size;

  @Input() styles: string = DEFAULT_VALUES.styles;

  @Input() validators = { required: true };

  @Input() variant = DEFAULT_VALUES.variant;

  @Input() showViewButton: any = DEFAULT_VALUES.showViewButton;

  @Output() getKeyValue: EventEmitter<any> = new EventEmitter<any>();

  @Output() getRemovedFile: EventEmitter<any> = new EventEmitter<any>();

  @Output() getViewButtonClick: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild("nativeFilePicker") nativeFilePicker: ElementRef;

  formGroup: FormGroup = new FormGroup({});

  openDropdown = false;

  normalFiles: any[] = [];

  errorFiles: any[] = [];

  loadingFiles: any[] = [];

  deletedFiles: any[] = [];

  subscriptions = new Subscription();

  constructor() {}

  ngOnInit(): void {
    this.formGroup.addControl(this.formControlName, this.formControl);
    this.validators = { ...this.validators };
    this.disabled = this.disabled;
    this.setDisabled();

    this.files = this.filterDuplicates(this.files);
    this.loadFiles(this.files);

    this.subscriptions.add(
      this.formGroup.controls[this.formControlName].valueChanges
        .pipe(distinctUntilChanged()) // makes sure the value has actually changed.
        .subscribe((value) => {
          this.handleInputValueChange(value);
        })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    const verifyChange = (key: string) => {
      return changes.hasOwnProperty(key) && !changes[key].firstChange;
    };

    for (const change of Object.entries(changes)) {
      const key: string = change[0];
      const value: any = change[1].currentValue;
      this.validateInputProperty(key, value);
    }

    if (verifyChange("formControl")) {
      this.formControl = changes.formControl.currentValue;
    }
    if (verifyChange("styles")) {
      this.styles = changes.styles.currentValue;
    }
    if (verifyChange("class")) {
      this.class = changes.class.currentValue;
    }
    if (verifyChange("multiple")) {
      this.multiple = changes.multiple.currentValue;
    }
    if (verifyChange("accept")) {
      this.accept = changes.accept.currentValue;
    }
    if (verifyChange("label")) {
      this.label = changes.label.currentValue;
    }
    if (verifyChange("link")) {
      this.link = changes.link.currentValue;
    }
    if (verifyChange("icon")) {
      this.icon = changes.icon.currentValue;
    }
    if (verifyChange("size")) {
      this.size = changes.size.currentValue;
    }
    if (verifyChange("setFileTypesToForm")) {
      this.setFileTypesToForm = changes.setFileTypesToForm.currentValue;
    }
    if (verifyChange("files")) {
      this.files = changes.files.currentValue;

      this.files = this.filterDuplicates(this.files);
      this.loadFiles(this.files);
    }
    if (verifyChange("maxFileSize")) {
      this.maxFileSize = changes.maxFileSize.currentValue;
    }
    if (verifyChange("disabled")) {
      this.disabled = changes.disabled.currentValue;

      this.setDisabled();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // ensure when component is destroyed the subscription is also and not left open.
  }

  private validateInputProperty(key: string, value: any): void {
    if (VALIDATE_KEY_VALUES[key] && !isValidKeyValue(key, value)) {
      this[key] = DEFAULT_VALUES[key];
      throwErrorMessage(
        "kwikui-input-files",
        this.id,
        key,
        value,
        DEFAULT_VALUES[key]
      );
    }
  }

  loadFiles(files: any[]) {
    this.normalFiles = files.filter(
      (file: any) => file.state === EKwikUIInputFileState.NORMAL
    );
    this.errorFiles = files.filter(
      (file: any) => file.state === EKwikUIInputFileState.ERROR
    );
    this.loadingFiles = files.filter(
      (file: any) => file.state === EKwikUIInputFileState.LOADING
    );
    this.deletedFiles = files.filter(
      (file: any) => file.state === EKwikUIInputFileState.DELETED
    );

    const setFiles = [
      ...(this.setFileTypesToForm.includes(EKwikUIInputFileState.NORMAL)
        ? this.normalFiles
        : []),
      ...(this.setFileTypesToForm.includes(EKwikUIInputFileState.ERROR)
        ? this.errorFiles
        : []),
      ...(this.setFileTypesToForm.includes(EKwikUIInputFileState.LOADING)
        ? this.loadingFiles
        : []),
      ...(this.setFileTypesToForm.includes(EKwikUIInputFileState.DELETED)
        ? this.deletedFiles
        : [])
    ];

    this.formControl.setValue(setFiles, { emitEvent: false });
  }

  filterDuplicates(files: any[]): any[] {
    const uniqueFiles: any[] = [];

    files.forEach((file) => {
      const isDuplicate = uniqueFiles.some(
        (existingFile) =>
          existingFile.name === file.name && existingFile.size === file.size
      );

      if (!isDuplicate) {
        uniqueFiles.push(file);
      }
    });

    return uniqueFiles;
  }

  filterUnique(arr1: any[], arr2: any[]): any[] {
    const uniqueElements: any[] = [];

    arr2.forEach((element) => {
      if (!arr1.includes(element)) {
        uniqueElements.push(element);
      }
    });

    return uniqueElements;
  }

  /**
   * @description Handles setting up of error and focus on the input field is it is invalid
   */
  setDisabled() {
    if (this.formGroup.controls[this.formControlName] !== undefined) {
      if (this.disabled === true) {
        this.formGroup.controls[this.formControlName].disable({
          emitEvent: false
        });
      } else {
        this.formGroup.controls[this.formControlName].enable({
          emitEvent: false
        });
      }
    }
  }

  handleOnClickOpenFileManager() {
    if (isNotEmptyValue(this.nativeFilePicker)) {
      this.nativeFilePicker.nativeElement.click();
    }
  }

  handleOnInputFileChange(e: any) {
    const files = [];

    for (const file of e.target.files) {
      files.push(file);
    }

    this.formGroup.patchValue(
      {
        [this.formControlName]: this.multiple ? files : files[0]
      },
      {
        emitEvent: true
      }
    );
  }

  /**
   * @description Handles firing of 2 events on (keyup) event
   *
   * @param value
   */
  handleInputValueChange(value: any) {
    if (Array.isArray(value)) {
      value = this.filterUnique(this.files, value);
    } else {
      value = this.filterUnique(this.files, [value]);
    }

    this.emitEvent(this.getKeyValue, {
      key: this.formControlName,
      value
    });
  }

  emitEvent(event: any, data: any) {
    event.emit(data);
  }

  removeFile(file: any) {
    const files = this.files.filter((f: any) => {
      return !(file.name === f.name && file.size === f.size);
    });

    this.emitEvent(this.getRemovedFile, {
      key: this.formControlName,
      value: file,
      files
    });
  }

  fileClick(file: any) {
    const files = this.files.filter((f: any) => {
      return !(file.name === f.name && file.size === f.size);
    });

    this.emitEvent(this.getViewButtonClick, {
      key: this.formControlName,
      value: file,
      files
    });
  }

  /** Method Implementations for Abstract Control */
  writeValue(value: any): void {}

  registerOnChange(fn: any): void {}

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}
}
